/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
/**
 * SceneExpand — shared expanded scene view used by Strip Board and Schedule.
 * Shows: scene metadata, breakdown items by category (with inline edit & add),
 * and a "remark" thread (internal comments with author + timestamp).
 *
 * Props:
 *   scene         — { id, scene_number, scene_header, location_name, location_type,
 *                     time_of_day, characters_in_scene, page_count_eighths,
 *                     synopsis, breakdown_items: { CATEGORY: [items] } }
 *   onClose       — () => void
 *   onSceneChange — () => void (refetch parent)
 */
import React, { useRef, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { X, Plus, Trash2, Send, MessageSquare, ChevronDown, ChevronRight, ImageIcon, Pencil, Sparkles, Palette } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): ImageThumbStrip
// was wired for the item-images strip but the JSX callsite was dropped in a
// prior refactor. Restore <ImageThumbStrip images={item.image_paths}/> in the
// breakdown-item render block OR remove `ImageThumbStrip` from this import
// list if the strip is intentionally gone.
import { ImageUploadButton, ImageThumbStrip } from './ImageUpload';
import { AuthImage } from './AuthImage';
import { toast } from 'sonner';
import { MLText } from './MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';
import BreakdownItemEditModal from '../pages/art/BreakdownItemEditModal';
import AiSuggestPanel from '../pages/art/AiSuggestPanel';

// Phase 13 (2026-07) — the 3 columns for which the backend
// `/art/suggest-props` prompt is trained. Do NOT wire the button on
// other categories (VFX / Costume / Stunt / etc).
const AI_COLUMN_FOR_CATEGORY = {
  PROP:             'action_prop',
  SCENE_PROP:       'scene_prop',
  ART_SET_DRESSING: 'set_dressing',
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ORDER = [
  { key: 'PROP', label: 'Props', color: 'text-blue-400' },
  { key: 'ART_SET_DRESSING', label: 'Set Dressing', color: 'text-purple-400' },
  { key: 'PICTURE_VEHICLE', label: 'Picture Vehicles', color: 'text-rose-400' },
  { key: 'COSTUME', label: 'Costumes', color: 'text-yellow-400' },
  { key: 'MAKEUP_HAIR', label: 'Makeup/Hair', color: 'text-pink-400' },
  { key: 'VFX', label: 'VFX', color: 'text-cyan-400' },
  { key: 'STUNT', label: 'Stunts', color: 'text-orange-400' },
  { key: 'SFX_SPECIAL_EFFECTS', label: 'SFX', color: 'text-red-400' },
  { key: 'VEHICLE', label: 'Vehicles (Prod)', color: 'text-amber-400' },
  { key: 'ANIMAL', label: 'Animals', color: 'text-green-400' },
  { key: 'EXTRA_BACKGROUND', label: 'Extras', color: 'text-lime-400' },
  { key: 'SOUND_MUSIC', label: 'Sound/Music', color: 'text-teal-400' },
];

const SceneExpand = ({ scene, onClose, onSceneChange }) => {
  const { projectId } = useParams();
  const { token, user } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [remarks, setRemarks] = useState([]);
  const [newRemark, setNewRemark] = useState('');
  const [posting, setPosting] = useState(false);
  const [addItemCategory, setAddItemCategory] = useState('PROP');
  const [addItemText, setAddItemText] = useState('');
  const [adding, setAdding] = useState(false);
  const [screenplayText, setScreenplayText] = useState('');
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [showScript, setShowScript] = useState(true);

  // Phase 13 (2026-07) — Ask A: edit modal state.
  const [editingItem, setEditingItem] = useState(null);
  // Phase 13 (2026-07) — Ask B: AI suggest panel state.
  const [aiPanelColumn, setAiPanelColumn] = useState(null);
  const [aiPanelAnchor, setAiPanelAnchor] = useState(null);
  const aiButtonRefs = useRef({});
  // Ask D — deep-link to Art Dept for Look & Feel work.
  const navigate = useNavigate();

  const fetchRemarks = async () => {
    try {
      const r = await axios.get(`${API}/scenes/${scene.id}/comments`, { headers });
      setRemarks((r.data || []).filter(c => c.is_remark));
    } catch (err) { console.error('Failed to fetch remarks:', err); /* non-critical */ }
  };

  const fetchScreenplay = async () => {
    try {
      const r = await axios.get(`${API}/scenes/${scene.id}/screenplay`, { headers });
      setScreenplayText(r.data?.scene_text || '');
    } catch (err) { console.error('Failed to fetch screenplay text:', err); /* non-critical */ }
  };

  useEffect(() => {
    if (scene?.id) { fetchRemarks(); fetchScreenplay(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene?.id]);

  const handlePostRemark = async () => {
    if (!newRemark.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/scenes/${scene.id}/comments`, {
        content: newRemark.trim(), is_remark: true
      }, { headers });
      setNewRemark('');
      fetchRemarks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post remark');
    } finally { setPosting(false); }
  };

  const handleDeleteRemark = async (remarkId) => {
    if (!window.confirm('Delete this remark?')) return;
    try {
      await axios.delete(`${API}/comments/${remarkId}`, { headers });
      fetchRemarks();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleQuickAddItem = async () => {
    if (!addItemText.trim()) return;
    setAdding(true);
    try {
      const descriptions = addItemText.split(',').map(s => s.trim()).filter(Boolean);
      await axios.post(`${API}/projects/${projectId}/breakdown-items/bulk`, {
        scene_ids: [scene.id],
        category: addItemCategory,
        descriptions
      }, { headers });
      toast.success(`Added ${descriptions.length} item(s)`);
      setAddItemText('');
      onSceneChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Add failed');
    } finally { setAdding(false); }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await axios.delete(`${API}/breakdown-items/${itemId}`, { headers });
      onSceneChange?.();
    } catch { toast.error('Delete failed'); }
  };

  const itemsByCategory = scene.breakdown_items || {};
  const totalItems = Object.values(itemsByCategory).reduce((sum, arr) => sum + (arr?.length || 0), 0);

  return (
    <div className="bg-[#0d0d0d] border border-[#e8c547]/30 rounded-lg overflow-hidden shadow-2xl shadow-black/50" data-testid={`scene-expand-${scene.scene_number ?? 'unnumbered'}`}>
      {/* Header */}
      <div className="px-5 py-3 bg-gradient-to-r from-[#1a1a1a] to-[#0d0d0d] border-b border-[#2a2a2a] flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-8 w-8 rounded bg-[#e8c547]/20 text-[#e8c547] font-bold text-sm flex items-center justify-center shrink-0">
            {formatSceneNumber(scene.scene_number)}
          </span>
          <div className="min-w-0">
            <div className="font-['DM_Serif_Display'] text-base text-[#f0f0f0] truncate">{scene.scene_header}</div>
            <div className="text-xs text-[#a0a0a0] flex items-center gap-2 mt-0.5">
              <span>{scene.location_type} · {scene.time_of_day}</span>
              <span>·</span>
              <span>{scene.location_name}</span>
              <span>·</span>
              <span>{(scene.page_count_eighths || 0) / 8} pg</span>
              {totalItems > 0 && (<><span>·</span><span className="text-[#e8c547]">{totalItems} items</span></>)}
            </div>
          </div>
        </div>
        <button onClick={onClose} className="h-7 w-7 rounded text-[#a0a0a0] hover:text-[#f0f0f0] flex items-center justify-center" data-testid="scene-expand-close">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Ask D — deep-link to Art Dept Look & Feel for this scene */}
      <div className="px-5 py-2 bg-[#0d0d0d] border-b border-[#2a2a2a] flex justify-end">
        <button
          onClick={() => navigate(`/projects/${projectId}/dept/art?scene=${scene.id}`)}
          className="text-[11px] text-[#e8c547] hover:text-[#f0f0f0] flex items-center gap-1"
          data-testid="scene-expand-look-feel-link">
          <Palette className="h-3 w-3" /> Look &amp; Feel in Art Dept
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x divide-[#2a2a2a]">
        {/* LEFT: Screenplay text + Collapsible Breakdown */}
        <div className="p-5 space-y-4">
          {/* Screenplay text (full scene as in script) */}
          <div>
            <button
              onClick={() => setShowScript(s => !s)}
              className="w-full flex items-center justify-between mb-2 group"
              data-testid="toggle-screenplay"
            >
              <div className="text-[10px] uppercase tracking-wider text-[#a0a0a0] group-hover:text-[#f0f0f0]">Screenplay</div>
              {showScript ? <ChevronDown className="h-3 w-3 text-[#a0a0a0]" /> : <ChevronRight className="h-3 w-3 text-[#a0a0a0]" />}
            </button>
            {showScript && (
              <div className="bg-[#0a0a0a] border border-[#2a2a2a] rounded p-4 max-h-[260px] overflow-y-auto" data-testid="screenplay-text">
                {screenplayText ? (
                  <pre className="font-mono text-[11px] leading-relaxed text-[#f0f0f0] whitespace-pre-wrap">{screenplayText}</pre>
                ) : (
                  <div className="text-xs text-[#a0a0a0] space-y-2">
                    <p className="font-mono uppercase text-[#f0f0f0]">{scene.scene_header}</p>
                    <p className="italic">{scene.synopsis}</p>
                    {scene.characters_in_scene?.length > 0 && (
                      <p className="text-[#a0a0a0]">Cast: {scene.characters_in_scene.join(', ')}</p>
                    )}
                    <p className="text-[10px] text-[#666] italic mt-2">(Raw screenplay text unavailable — showing extracted summary)</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Collapsible Breakdown */}
          <div>
            <button
              onClick={() => setShowBreakdown(b => !b)}
              className="w-full flex items-center justify-between mb-2 group"
              data-testid="toggle-breakdown"
            >
              <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase tracking-wider text-[#a0a0a0] group-hover:text-[#f0f0f0]">Breakdown</div>
                <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0] text-[10px]">{totalItems}</Badge>
              </div>
              {showBreakdown ? <ChevronDown className="h-3 w-3 text-[#a0a0a0]" /> : <ChevronRight className="h-3 w-3 text-[#a0a0a0]" />}
            </button>
            {showBreakdown && (
              <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                {CATEGORY_ORDER.map(cat => {
                  const items = itemsByCategory[cat.key] || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={cat.key} className="bg-[#1a1a1a] rounded p-2.5 border border-[#2a2a2a]" data-testid={`expand-cat-${cat.key}`}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className={`text-xs font-semibold ${cat.color}`}>{cat.label}</span>
                        <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0] text-[10px]">{items.length}</Badge>
                        {/* Ask B — AI suggest button, only on the 3 backend-supported columns */}
                        {AI_COLUMN_FOR_CATEGORY[cat.key] && (
                          <button
                            type="button"
                            ref={(el) => { aiButtonRefs.current[cat.key] = el; }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setAiPanelColumn(AI_COLUMN_FOR_CATEGORY[cat.key]);
                              setAiPanelAnchor(e.currentTarget.getBoundingClientRect());
                            }}
                            className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border border-[#3a3a2e] text-[#e8c547] hover:bg-[#e8c547]/10"
                            title="AI suggest for this scene"
                            data-testid={`scene-expand-ai-suggest-${cat.key}`}>
                            <Sparkles className="h-3 w-3" /> AI suggest
                          </button>
                        )}
                      </div>
                      <div className="space-y-1">
                        {items.map(it => (
                          <div key={it.id} className="flex items-center gap-2 group/it" data-testid={`expand-item-${it.id}`}>
                            <div className="h-7 w-7 rounded bg-[#0d0d0d] flex items-center justify-center shrink-0 overflow-hidden">
                              {it.image_paths?.length > 0 ? (
                                <AuthImage path={it.image_paths[0]} alt="" className="h-7 w-7 object-cover" />
                              ) : (
                                <ImageIcon className="h-3 w-3 text-[#444]" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <MLText as="div" className="text-xs text-[#f0f0f0] truncate">{getBreakdownItemLabel(it.description, 'original')}</MLText>
                              {it.notes && <div className="text-[10px] text-[#a0a0a0] truncate">{it.notes}</div>}
                            </div>
                            {/* Ask A — always-visible pencil to open full-field edit modal */}
                            <button
                              onClick={() => setEditingItem(it)}
                              className="h-6 w-6 rounded text-[#a0a0a0] hover:text-[#e8c547] flex items-center justify-center"
                              title="Edit item"
                              data-testid={`expand-item-edit-${it.id}`}>
                              <Pencil className="h-3 w-3" />
                            </button>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover/it:opacity-100 transition-opacity">
                              <ImageUploadButton itemId={it.id} onUploaded={onSceneChange} />
                              <button onClick={() => handleDeleteItem(it.id)} className="h-6 w-6 rounded text-[#a0a0a0] hover:text-red-400 flex items-center justify-center">
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {totalItems === 0 && (
                  <div className="text-xs text-[#444] italic text-center py-4">No breakdown items yet — add some below</div>
                )}
              </div>
            )}
          </div>

          {/* Quick add */}
          <div className="border-t border-[#2a2a2a] pt-3">
            <div className="text-[10px] uppercase tracking-wider text-[#a0a0a0] mb-2">Quick Add (comma-separated)</div>
            <div className="flex gap-2">
              <Select value={addItemCategory} onValueChange={setAddItemCategory}>
                <SelectTrigger className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] h-9 w-44 text-xs" data-testid="expand-add-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0]">
                  {CATEGORY_ORDER.map(c => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={addItemText}
                onChange={e => setAddItemText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleQuickAddItem(); }}
                placeholder="coffee mug, leather wallet, brass key…"
                className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] h-9 text-xs"
                data-testid="expand-add-input"
              />
              <Button onClick={handleQuickAddItem} disabled={adding} size="sm" className="bg-[#e8c547] hover:bg-[#d6b539] text-black h-9" data-testid="expand-add-btn">
                <Plus className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* RIGHT: Remark thread */}
        <div className="p-5 space-y-3 bg-[#0a0a0a]/40">
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare className="h-4 w-4 text-[#e8c547]" />
            <div className="text-sm font-semibold text-[#f0f0f0]">Remarks</div>
            <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0] text-[10px]">{remarks.length}</Badge>
            <span className="text-[10px] text-[#a0a0a0] ml-auto italic">Internal · not in exports</span>
          </div>

          <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
            {remarks.length === 0 && (
              <div className="text-xs text-[#444] italic text-center py-4">
                No remarks yet. Add internal notes here — they'll appear next to this scene in the Master Breakdown but won't be included in PDF/Excel exports.
              </div>
            )}
            {remarks.map(r => (
              <div key={r.id} className="bg-[#1a1a1a] border border-[#2a2a2a] rounded p-2.5 group/rem" data-testid={`remark-${r.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="h-5 w-5 rounded-full bg-[#e8c547]/20 text-[#e8c547] text-[10px] font-bold flex items-center justify-center shrink-0">
                      {r.user_name?.[0]?.toUpperCase() || '?'}
                    </div>
                    <span className="text-xs font-medium text-[#f0f0f0] truncate">{r.user_name}</span>
                    <span className="text-[10px] text-[#a0a0a0]">{r.user_department}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-[#a0a0a0]">{new Date(r.created_at).toLocaleString()}</span>
                    {(user?.id === r.user_id || user?.role === 'ADMIN') && (
                      <button
                        onClick={() => handleDeleteRemark(r.id)}
                        className="h-5 w-5 rounded text-[#a0a0a0] hover:text-red-400 flex items-center justify-center opacity-0 group-hover/rem:opacity-100"
                        data-testid={`delete-remark-${r.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-[#f0f0f0] whitespace-pre-wrap break-words">{r.content}</p>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2 border-t border-[#2a2a2a]">
            <Textarea
              value={newRemark}
              onChange={e => setNewRemark(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handlePostRemark(); }}
              placeholder="Add an internal remark…"
              className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] text-xs min-h-[60px] resize-none"
              data-testid="remark-input"
            />
            <Button
              onClick={handlePostRemark}
              disabled={posting || !newRemark.trim()}
              size="sm"
              className="bg-[#e8c547] hover:bg-[#d6b539] text-black self-end"
              data-testid="remark-post-btn"
            >
              <Send className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* Ask A — full-field edit modal (reused from Art Dept) */}
      <BreakdownItemEditModal
        open={!!editingItem}
        item={editingItem}
        projectId={projectId}
        token={token}
        onClose={() => setEditingItem(null)}
        onSaved={() => { setEditingItem(null); onSceneChange?.(); }}
      />
      {/* Ask B — AI suggest panel (reused from Art Dept) */}
      <AiSuggestPanel
        open={!!aiPanelColumn}
        anchorRect={aiPanelAnchor}
        projectId={projectId}
        token={token}
        sceneId={scene.id}
        column={aiPanelColumn || 'scene_prop'}
        existingNames={(() => {
          if (!aiPanelColumn) return [];
          // Map panel column back to CATEGORY_ORDER key for the existing-names hint.
          const backMap = { action_prop: 'PROP', scene_prop: 'SCENE_PROP', set_dressing: 'ART_SET_DRESSING' };
          const cat = backMap[aiPanelColumn];
          return (itemsByCategory[cat] || []).map(i => i.description).filter(Boolean);
        })()}
        onClose={() => { setAiPanelColumn(null); setAiPanelAnchor(null); }}
        onChipAdded={() => onSceneChange?.()}
      />
    </div>
  );
};

export default SceneExpand;
